import md5 from 'md5';
import Immutable from 'immutable';
import LHX from './LHX';
import { readCookie } from './utils';

export function getInsuranceLabelByType(type) {
  return {
    STANDARD: 'Standard',
    PREMIER: 'Premier',
    SINGLE_TRIP: 'Single Trip',
    ANNUAL_MULTI_TRIP: 'Annual Trip',
    INDIVIDUAL: 'Individual',
    FAMILY: 'Family',
  }[type];
}

export function getInsuranceDescriptionByType(coverType, currency) {
  let description = null;
  if (coverType === 'STANDARD') {
    description = `Key benefits include ${currency}3,000,000 medical expenses, ${currency}500 cancellation, ${currency}200 personal possessions`;
  } else if (coverType === 'PREMIER') {
    description = `Key benefits include ${currency}10,000,000 medical expenses, ${currency}2,000 cancellation, ${currency}1,500 personal possessions`;
  }

  return description;
}

export function formatTime(timeStr) {
  return timeStr.slice(0, -3);
}

export function rangeInclusive(start, end) {
  return Array(...Array((end + 1) - start))
    .map((value, index) => (index) + start);
}

export function isNumberOrPlusOnly(value) {
  return value.replace(/^[0-9+][0-9]*/, '').length === 0;
}

export function getCriteoSiteType() {
  // Approach recommended by Criteo if nothing else in place.
  // https://support.criteo.com/hc/en-us/articles/202806931-Managing-your-different-site-types
  const userAgent = window.navigator.userAgent;
  const siteTypeCodes = {
    desktop: 'd',
    tablet: 't',
    mobile: 'm',
  };
  let siteType;

  if (/iPad/.test(userAgent)) {
    siteType = 'tablet';
  } else if (/Mobile|iP(hone|od)|Android|BlackBerry|IEMobile|Silk/.test(userAgent)) {
    siteType = 'mobile';
  } else {
    siteType = 'desktop';
  }

  return siteTypeCodes[siteType];
}

export function initCriteo() {
  const scriptId = 'lh-criteo-script';

  if (!window.document.getElementById(scriptId)) {
    const script = window.document.createElement('script');
    script.type = 'text/javascript';
    script.async = true;
    script.id = scriptId;
    script.src = 'https://static.criteo.net/js/ld/ld.js';
    window.document.head.appendChild(script);
  }
}

/* eslint-disable no-console */
/* istanbul ignore next */
export function fireCriteoEvent(data) {
  const isLiveEnvironment = LHX.environment === 'prod' || LHX.environment === 'staging';
  const criteoAccount = 4498;

  if (!window.criteo_q && isLiveEnvironment) {
    initCriteo();
  }

  const eventPayload = [
    { event: 'setAccount', account: criteoAccount },
    { event: 'setCustomerId', id: readCookie('lvhid') },
    { event: 'setSiteType', type: getCriteoSiteType() },
    {
      event: 'viewSearch',
      checkin_date: data.outboundDeptDate,
      checkout_date: data.inboundDeptDate,
      nba: data.numberOfAdults,
      nbc: data.numberOfChildren,
      nbr: data.numberOfPassengers,
    },
    {
      event: 'viewBasket',
      item: [{
        id: data.masterId,
        price: data.totalPrice,
        quantity: 1,
      }],
    },
  ];

  window.criteo_q = window.criteo_q || [];
  window.criteo_q.push(eventPayload);
  if (__DEBUG__) console.debug('fireCriteoEvent', eventPayload);
}

export function fireCriteoEmailEvent(email) {
  const eventPayload = { event: 'setHashedEmail', email: md5(email) };

  window.criteo_q = window.criteo_q || [];
  window.criteo_q.push(eventPayload);
  if (__DEBUG__) console.debug('fireCriteoEmailEvent', eventPayload);
}

export function fireCriteoTransactionEvent(data) {
  const eventPayload = {
    event: 'trackTransaction',
    id: data.bookingReference,
    item: [{
      id: data.masterId,
      price: data.webSellingPrice && data.webSellingPrice / 100,
      quantity: 1,
    }],
  };

  window.criteo_q = window.criteo_q || [];
  window.criteo_q.push(eventPayload);
  if (__DEBUG__) console.debug('fireCriteoTransactionEvent', eventPayload);
}
/* eslint-enable no-console */

export const transfersTitleByType = {
  SHUTTLE: 'Shuttle Transfer',
  EXPRESS: 'Express Transfer',
  PRIVATE: 'Private Transfer',
  WHEELCHAIR: 'Wheelchair Transfer',
};

export const parkingTitleByType = {
  ON_AIRPORT: 'On Airport Parking',
  OFF_AIRPORT: 'Off Airport Parking',
  VALET_PARKING: 'Meet and Greet Parking',
};

export function isMinDiscount(discountAmount, wasPrice) {
  const minDiscountFraction = 0.05;
  return discountAmount > wasPrice * minDiscountFraction;
}

export function getFieldKeysByPassenger(bookState) {
  const fieldKeys = [
    'title',
    'firstName',
    'lastName',
    'dateOfBirthDay',
    'dateOfBirthMonth',
    'dateOfBirthYear',
  ];
  let leadFieldKeys = fieldKeys.concat([
    'email',
    'confirmEmail',
    'telephone',
    'address1',
    'address2',
    'city',
    'county',
    'postcode',
  ]);

  if (bookState.getIn(['booking_info', 'selection', 'parking_selected'], 0) !== 0) {
    leadFieldKeys = leadFieldKeys.concat([
      'vehicleMake',
      'vehicleModel',
      'vehicleRegistration',
      'vehicleColour',
    ]);
  }

  let fieldKeysByPassenger = Immutable.Map();

  bookState.getIn(['entities', 'passengers']).forEach((passenger) => {
    const passengerId = passenger.get('id');
    const isLeadPassenger = passengerId === bookState.getIn(['booking_info', 'leadPassengerId'], 0);
    fieldKeysByPassenger = fieldKeysByPassenger.set(`${passengerId}`, (isLeadPassenger ? leadFieldKeys : fieldKeys));
  });

  return fieldKeysByPassenger;
}

export function getFieldKeysByPassengerHO(bookState) {
  const fieldKeys = [
    'title',
    'firstName',
    'lastName',
  ];
  const leadFieldKeys = fieldKeys.concat([
    'email',
    'telephone',
    'address1',
    'address2',
    'city',
    'county',
    'postcode',
  ]);

  let fieldKeysByPassenger = Immutable.Map();

  bookState.getIn(['entities', 'passengers']).forEach((passenger) => {
    const passengerId = passenger.get('id');
    const isLeadPassenger = passengerId === bookState.getIn(['booking_info', 'leadPassengerId'], 0);
    fieldKeysByPassenger = fieldKeysByPassenger.set(`${passengerId}`, (isLeadPassenger ? leadFieldKeys : fieldKeys));
  });

  return fieldKeysByPassenger;
}



/** WEBPACK FOOTER **
 ** ./src/utils/book.js
 **/